import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  ExpressCheckoutPartialAddress,
  LineItem,
  ShippingRate,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from '@stripe/stripe-js';
import StripeElementsProvider, {
  HARDWARE_FIRST_APPEARANCE,
  HARDWARE_FIRST_GIFT_APPEARANCE,
} from '../../lib/StripeElementsProvider';
import CheckoutContext from '../../lib/CheckoutContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useCartPricing } from '../../contexts/CartPricingContext';
import useShippingOptions from '../../hooks/useShippingOptions';
import useHardwareFirstApplePayOption from '../../hooks/useHardwareFirstApplePayOption';
import { priceCartForCheckout } from '../../lib/pricing';
import { isExpeditedShippingAvailableToState } from '../../lib/shipping';
import { getLocalizedPricing } from '../../lib/util/money';
import { parseStripeAddressName } from '../../lib/nameParser';
import { cleanName, isShippingName, sanitizeExpressAddress } from '../../lib/expressAddress';
import { logInternalError } from '../../lib/errors';
import * as events from '../../lib/analytics/events';
import * as types from '../../types';
import type { StripeCheckoutSubmission } from '../StripeCheckout';
import styles from './ExpressCheckoutRow.module.scss';

interface ExpressCheckoutRowProps {
  /** the sheet's pay line until it has an address: the cart's preview total */
  amountInCents: number;
  /** the same submission the form builds; the purchase hook takes it from here */
  onSubmitCheckout: (submission: StripeCheckoutSubmission) => void;
  /** the purchase hook is busy, so a second tap must not start another purchase */
  disabled?: boolean;
  error?: string;
  giftOrder?: boolean;
}

/**
 * What the sheet lists above its total. Apple's recurring request has no slot for a one-time
 * item, so without these the tracker only appears in the description.
 */
function sheetLineItems(pricing: types.CartPricing, t: (key: string) => string): LineItem[] {
  const tax = pricing.taxInCents ?? 0;
  const shipping = pricing.shippingInCents ?? 0;
  return [
    { name: t('hardware_first_checkout_hardware'), amount: pricing.totalInCents - tax - shipping },
    ...(shipping > 0 ? [{ name: t('shipping'), amount: shipping }] : []),
    ...(tax > 0 ? [{ name: t('tax'), amount: tax }] : []),
  ];
}

function toShippingRate(option: types.IShippingOption, currency: types.FiCurrency): ShippingRate {
  return {
    id: option.code,
    displayName: option.name,
    amount: getLocalizedPricing(option.pricingInCents, currency),
  };
}

/**
 * Apple pay / google pay buttons above the checkout form. The wallet sheet collects email, phone,
 * shipping address and the shipping method, and the confirm hands the purchase hook the same
 * submission the form builds, so inline login, 3ds, errors and thank-you are shared.
 *
 * The row runs in its own stripe elements group: a payment element that shares a group with an
 * express element drops its own wallet rows, and the row needs payment mode with the real amount
 * while the form's group stays in setup mode.
 */
export default function ExpressCheckoutRow({ amountInCents, giftOrder = false, ...rest }: ExpressCheckoutRowProps) {
  const { currency } = useLocale();
  return (
    <StripeElementsProvider
      mode="payment"
      amountInCents={amountInCents}
      currency={currency}
      appearance={giftOrder ? HARDWARE_FIRST_GIFT_APPEARANCE : HARDWARE_FIRST_APPEARANCE}
    >
      <ExpressCheckoutButtons amountInCents={amountInCents} {...rest} />
    </StripeElementsProvider>
  );
}

function ExpressCheckoutButtons({ amountInCents, onSubmitCheckout, disabled = false, error }: ExpressCheckoutRowProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { cart } = useContext(CheckoutContext);
  const previewPricing = useCartPricing();
  const { currency, supportedCountries } = useLocale();
  const { shippingOptions, defaultShippingCode } = useShippingOptions();
  const { applePayRecurring, trialPending } = useHardwareFirstApplePayOption();
  // stripe reports which wallets this browser can show; until then the row takes no space
  const [available, setAvailable] = useState(false);
  // the address the open sheet last reported, so a shipping method pick can re-price against it
  const sheetAddressRef = useRef<ExpressCheckoutPartialAddress | undefined>(undefined);

  // the sheet's amount follows the cart (a promo applied on the page) through the group itself:
  // the provider's options are fixed after mount
  useEffect(() => {
    elements?.update({ amount: amountInCents });
  }, [elements, amountInCents]);

  // the sheet redacts the street until confirm, so only the state can rule expedited out here;
  // the server still rejects expedited to a po box at purchase
  const optionsForState = useCallback(
    (state?: string) =>
      shippingOptions.filter((option) => !option.isExpedited || !state || isExpeditedShippingAvailableToState(state)),
    [shippingOptions],
  );

  const shippingCodeFor = useCallback(
    (rateId: string | undefined) =>
      shippingOptions.find((option) => option.code === rateId)?.code ?? defaultShippingCode,
    [shippingOptions, defaultShippingCode],
  );

  // tax and shipping need the zip, which the sheet only has once an address is picked
  const repriceSheet = useCallback(
    async (shippingCode: types.ShippingCode, address: ExpressCheckoutPartialAddress) => {
      const pricing = await priceCartForCheckout(cart, {
        shippingCode,
        address: { country: address.country, postalCode: address.postal_code },
      });
      elements?.update({ amount: pricing.totalInCents });
      return sheetLineItems(pricing, t);
    },
    [cart, elements, t],
  );

  const handleReady = useCallback(({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
    setAvailable(!!availablePaymentMethods?.applePay || !!availablePaymentMethods?.googlePay);
  }, []);

  const handleClick = useCallback(
    ({ resolve, expressPaymentType }: StripeExpressCheckoutElementClickEvent) => {
      events.payment.expressCheckoutBegin(expressPaymentType);
      resolve({
        lineItems: sheetLineItems(previewPricing, t),
        shippingRates: optionsForState().map((option) => toShippingRate(option, currency)),
      });
    },
    [optionsForState, currency, previewPricing, t],
  );

  // the sheet selects the first rate of a new list, so the amount is priced for that one
  const handleShippingAddressChange = useCallback(
    async ({ address, resolve, reject }: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
      sheetAddressRef.current = address;
      const options = optionsForState(address.state);
      try {
        const lineItems = await repriceSheet(options[0]?.code ?? defaultShippingCode, address);
        resolve({ lineItems, shippingRates: options.map((option) => toShippingRate(option, currency)) });
      } catch (err) {
        logInternalError(err instanceof Error ? err : new Error(String(err)), {
          fingerprint: ['express-checkout-reprice'],
        });
        reject();
      }
    },
    [optionsForState, repriceSheet, defaultShippingCode, currency],
  );

  const handleShippingRateChange = useCallback(
    async ({ shippingRate, resolve, reject }: StripeExpressCheckoutElementShippingRateChangeEvent) => {
      const address = sheetAddressRef.current;
      if (!address) {
        resolve();
        return;
      }
      try {
        resolve({ lineItems: await repriceSheet(shippingCodeFor(shippingRate.id), address) });
      } catch (err) {
        logInternalError(err instanceof Error ? err : new Error(String(err)), {
          fingerprint: ['express-checkout-reprice'],
        });
        reject();
      }
    },
    [repriceSheet, shippingCodeFor],
  );

  const handleCancel = useCallback(() => {
    sheetAddressRef.current = undefined;
    elements?.update({ amount: amountInCents });
  }, [elements, amountInCents]);

  const handleConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      const { billingDetails, shippingAddress, shippingRate, expressPaymentType } = event;
      // apple pay leaves billingDetails.name empty; the shipping contact has the name
      const name = cleanName(billingDetails?.name || shippingAddress?.name || '');
      const parsed = parseStripeAddressName({ name });
      // the purchase refuses a last name under two letters, so a one-word contact name is both names
      const firstName = parsed.firstName;
      const lastName = isShippingName(parsed.lastName) ? parsed.lastName : parsed.firstName;
      if (!stripe || !elements || !shippingAddress || !isShippingName(firstName)) {
        event.paymentFailed({ reason: 'fail', message: t('express_checkout_name_required') });
        events.payment.expressCheckoutError(expressPaymentType, 'missing_name');
        logInternalError(new Error('express checkout wallet returned no usable name'), {
          tags: { checkoutStep: 'expressCheckout', expressPaymentType },
          extra: { hasShippingAddress: !!shippingAddress, nameLength: name.length },
        });
        return;
      }

      // as typed on the contact card: "Fl" for the state, stray spaces; the purchase wants codes
      const address = sanitizeExpressAddress(shippingAddress.address);
      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        params: {
          return_url: `${window.location.origin}/checkout/stripe`,
          // the wallet puts only the email and phone on its payment method, and recurly charges
          // the token against a billing address, so the shipping contact is the billing contact
          // here as it is on the form
          payment_method_data: {
            billing_details: {
              name,
              email: billingDetails?.email ?? '',
              phone: billingDetails?.phone ?? '',
              address: { ...address, line2: address.line2 ?? '' },
            },
          },
        },
      });
      if (tokenError || !confirmationToken) {
        // the last point paymentFailed may run: stripe closes the sheet once a token exists, and
        // a later call throws. errors after this render on the page.
        event.paymentFailed({ reason: 'fail' });
        events.payment.expressCheckoutError(expressPaymentType, tokenError?.message);
        logInternalError(new Error(tokenError?.message ?? 'express checkout token creation failed'), {
          tags: { checkoutStep: 'expressCheckout', expressPaymentType },
        });
        return;
      }

      const card = confirmationToken.payment_method_preview?.card;
      onSubmitCheckout({
        tokenId: confirmationToken.id,
        email: billingDetails?.email ?? '',
        address: {
          name,
          firstName,
          lastName,
          address,
          // as the wallet formats it; web normalizes the number per country, as it does the form's
          phone: billingDetails?.phone,
        },
        shippingCode: shippingCodeFor(shippingRate?.id),
        paymentMethodDetails: {
          type: 'card',
          brand: card?.brand ?? '',
          last4: card?.last4 ?? '',
          expMonth: card?.exp_month ?? 0,
          expYear: card?.exp_year ?? 0,
        },
        // the wallet verified the email, so an existing fi account logs in without a password
        isIdentityVerified: true,
        paymentType: expressPaymentType,
        // a payment-mode token: recurly charges it with the purchase instead of storing it first
        chargedInline: true,
      });
    },
    [stripe, elements, shippingCodeFor, onSubmitCheckout, t],
  );

  const options = useMemo(
    () => ({
      shippingAddressRequired: true,
      emailRequired: true,
      phoneNumberRequired: true,
      allowedShippingCountries: supportedCountries.map(String),
      paymentMethods: {
        applePay: 'auto' as const,
        googlePay: 'auto' as const,
        link: 'never' as const,
        paypal: 'never' as const,
        amazonPay: 'never' as const,
        klarna: 'never' as const,
      },
      buttonType: { applePay: 'buy' as const, googlePay: 'buy' as const },
      // the merchant token request; without it renewals land on a device token
      ...(applePayRecurring && { applePay: applePayRecurring }),
    }),
    [supportedCountries, applePayRecurring],
  );

  // a trial cart whose terms are not priced yet offers no apple pay anywhere
  if (trialPending) {
    return null;
  }

  return (
    <div
      className={styles.row}
      style={available ? undefined : { visibility: 'hidden', height: 0, overflow: 'hidden', margin: 0 }}
    >
      <div className={classNames({ [styles.busy]: disabled })}>
        <ExpressCheckoutElement
          options={options}
          onReady={handleReady}
          onClick={handleClick}
          onConfirm={(event) => void handleConfirm(event)}
          onShippingAddressChange={(event) => void handleShippingAddressChange(event)}
          onShippingRateChange={(event) => void handleShippingRateChange(event)}
          onCancel={handleCancel}
        />
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.divider}>
        <span>{t('or_pay_with')}</span>
      </div>
    </div>
  );
}
